import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import VerifyEmail from './pages/VerifyEmail';
import Portal from './pages/Portal';
import AgentLayout from './pages/AgentLayout';
import AgentIdentity from './pages/AgentIdentity';
import AgentTeaching from './pages/AgentTeaching';
import AgentEscalation from './pages/AgentEscalation';
import AgentKnowledgeBase from './pages/AgentKnowledgeBase';
import AgentMasterMemory from './pages/AgentMasterMemory';
import AgentLogs from './pages/AgentLogs';
import AgentEscalationLogs from './pages/AgentEscalationLogs';
import AgentKnowledgeFiles from './pages/AgentKnowledgeFiles';
import AgentEnvironment from './pages/AgentEnvironment';
import SkillWorkspace from './pages/SkillWorkspace';
import ConnectionsMap from './pages/ConnectionsMap';
import WorkflowBuilder from './pages/WorkflowBuilder';
import Docs from './pages/Docs';
import Pricing from './pages/Pricing';
import Settings from './pages/Settings';
import { PrivacyPolicy, TermsOfService, CookiesPolicy } from './pages/Legal';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (user) {
    return <Navigate to="/portal" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={
        <PublicRoute>
          <Auth />
        </PublicRoute>
      } />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/portal" element={
        <ProtectedRoute>
          <Portal />
        </ProtectedRoute>
      } />
      <Route path="/portal/agent/:agentId" element={
        <ProtectedRoute>
          <AgentLayout />
        </ProtectedRoute>
      }>
        <Route path="builder" element={<WorkflowBuilder />} />
        <Route path="identity" element={<AgentIdentity />} />
        <Route path="teaching" element={<AgentTeaching />} />
        <Route path="knowledge-files" element={<AgentKnowledgeFiles />} />
        <Route path="environment" element={<AgentEnvironment />} />
        <Route path="additional-info" element={<Navigate to="../knowledge-files" replace />} />
        <Route path="connections" element={<Navigate to="../builder" replace />} />
        <Route path="listeners" element={<Navigate to="../builder" replace />} />
        <Route path="subagents" element={<Navigate to="../builder" replace />} />
        <Route path="skills/:skillId" element={<SkillWorkspace />} />
        <Route path="escalation" element={<AgentEscalation />} />
        <Route path="escalation-logs" element={<AgentEscalationLogs />} />
        <Route path="knowledge" element={<AgentKnowledgeBase />} />
        <Route path="master-memory" element={<AgentMasterMemory />} />
        <Route path="logs" element={<AgentLogs />} />
        <Route index element={<Navigate to="builder" replace />} />
      </Route>
      <Route path="/docs" element={<Docs />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="app">
            <AppRoutes />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
